import { describeTransactionProperties } from './transactionText'
import {
  ESTIMATE_SECTION_DESCRIPTIONS,
  withDescriptions,
} from './sectionDescriptions'
import type { AiHandoffFigure, AiHandoffSectionId } from '@popety-io/types'

/**
 * One extractable value inside a stored section.
 *
 * Sections are declared as field maps rather than hand-written mappers for one
 * reason: a bare `livingArea: 200` reached production because a unit was
 * forgotten in a single place. Here the unit is part of declaring the field at
 * all, so it cannot be quietly dropped across a dozen more sections.
 *
 * Every path below was read off production data, never off the TypeScript
 * types — 16 of the 21 stored sections are typed `any`. A first draft of this
 * file guessed `scores.publicTransport` correctly but also `annual.totalSunHours`,
 * `publicTransportMetrics.nearestStopDistance` and flat `road_day` numbers, all
 * of which do not exist. Verify before adding.
 */
export interface SectionField {
  /** Dot path inside the stored section value. */
  path: string
  /** Stable, machine-readable key emitted in the payload. */
  key: string
  /**
   * Omit only for values that genuinely have no unit — enums, names, dates.
   * Never omit it to avoid deciding: a number with no unit is the defect this
   * whole payload exists to remove.
   */
  unit?: AiHandoffFigure['unit']
  source: AiHandoffFigure['source']
  /**
   * Applied to a numeric value before it is emitted. Exists because stored
   * units and public-API units differ in places: ElCom electricity prices are
   * stored in Rp./kWh and the public API publishes CHF/kWh, so emitting the
   * stored number under a CHF/kWh label would be wrong by a factor of 100.
   */
  /**
   * Returning `null` marks the value KNOWN-UNKNOWN: the field is still emitted,
   * as `{ value: null, unit, … }`, so a reader can tell "we looked and the
   * source does not say" apart from a field that is simply absent. Sentinels
   * (-1 areas, 0 prices meaning "on request") use this.
   */
  transform?: (v: number) => number | null
  /**
   * Turns the value at `path` into what is emitted, for text that has to be
   * composed from a structured subtree: the object of a land-registry
   * transaction is described from `transaction_properties` (legal type, share,
   * lot, units) because the registry's own sentence names the parties. The
   * result goes through the same emission rules as a stored value.
   */
  derive?: (raw: unknown) => unknown
  /**
   * `list` emits an array of primitives as-is — used for listing image URLs,
   * which are a genuine array of strings rather than one value. Without it the
   * array is silently dropped, because only primitives are emitted.
   *
   * `length` emits the size of the array at `path` instead of its contents.
   * Used next to a `series` that only picks safe columns: land-registry
   * transactions carry former owners' names and dates of birth in
   * `transaction_raw_data`, and permits carry LLM-derived classifications,
   * so the rows are declared column by column and never copied whole.
   */
  kind?: 'value' | 'length' | 'series' | 'object' | 'list'
  /**
   * For `series`: the fields to pick from each row. An array is mapped
   * directly; an object keyed by period (market quarters) becomes rows with
   * that key emitted under `period_key`.
   */
  item?: SectionField[]
  /**
   * Picks the unit from a sibling field, for series whose rows mix quantities.
   * A listings array holds both sales and lets: the same `price` is CHF on a
   * `deal_type` of "purchase" and CHF/month on "rent". A static unit would
   * label a monthly rent as a sale price. Falls back to `unit` when the
   * sibling's value is not in the map.
   */
  unit_by?: {
    path: string
    map: Record<string, AiHandoffFigure['unit']>
  }
  /**
   * For `kind: 'list'` over an array of OBJECTS: the field to pluck from each,
   * e.g. a broker's `contact_names[].name`. Omit for an array of strings.
   */
  list_path?: string
  /** For `series` over a keyed object: the name to give the key. */
  period_key?: string
  /**
   * For `series` over an array: order the rows by this path, newest first, and
   * keep at most `limit`. A property's history can hold fifty land-registry
   * mutations; a reader needs the recent ones in order, not fifty in storage
   * order.
   */
  sort_desc_by?: string
  limit?: number
  /** For string fields: keep only the first `max_chars` characters (free text). */
  max_chars?: number
  /**
   * Where to read the outer key from, when it is a field of the row rather
   * than the object key or an aggregation bucket's `key`. Saved listings are a
   * plain array, so their price history is labelled by `address`.
   */
  period_path?: string
  /**
   * For `series`: a path to a nested array inside each outer row, flattened
   * into the same series so one row carries both keys. Land and building
   * markets nest price buckets two deep (`by_category.buckets[].by_rooms
   * .buckets[]`); emitting that as rows of `{property_category, rooms_nb, …}`
   * keeps it chartable, which nested arrays of figures would not be.
   */
  explode?: string
  /**
   * For a quarterly `series`: also emit, under `key` and BEFORE the series, the
   * last four quarters against the same four quarters one and five years
   * earlier. `value_key` is averaged over each window weighted by `weight_key`.
   * A reader that stops before the end of a long series still has the trend.
   */
  trend?: { key: string; value_key: string; weight_key: string }
  /** The name to give the exploded row's own `key`. */
  explode_key?: string
  /**
   * Drops exploded rows whose numeric key falls below this. Room-count
   * aggregations bucket "unknown" as -1 and 0; emitting those as room counts
   * would hand a model two impossible values. The application filters them the
   * same way (`b.key > 0`).
   */
  explode_key_min?: number
}

export interface SectionSpec {
  id: AiHandoffSectionId
  /**
   * The key this section is stored under, when it differs from `id`. Land and
   * building shares store both markets under one key each with the payload
   * nested inside (`purchase_market.purchase`), and a couple of sections are
   * reused across artifacts under different names. Defaults to `id`.
   */
  source_key?: string
  /** Heading used in the rendered Markdown. */
  title: string
  /** Credited under the heading when the data is not ours. */
  provenance?: { provider: string; reference_url?: string }
  /** Copied onto the emitted section; see `AiHandoffSection.definitions`. */
  definitions?: Record<string, string>
  /** Copied onto the emitted section; see `AiHandoffSection.description`. */
  description?: string
  fields: SectionField[]
}

/** Whole units — a mean price of 2'221'877.4135977337 asserts precision it has not got. */
export const round0 = (v: number): number => Math.round(v)
/** One decimal, for areas and room counts. */
export const round1 = (v: number): number => Math.round(v * 10) / 10

export const money = (path: string, key: string): SectionField => ({
  path,
  key,
  unit: 'CHF',
  source: 'spatial_analysis',
  transform: round0,
})

export const area = (path: string, key: string): SectionField => ({
  path,
  key,
  unit: 'm2',
  source: 'spatial_analysis',
  transform: round1,
})

export const rooms = (path: string, key: string): SectionField => ({
  path,
  key,
  unit: 'count',
  source: 'spatial_analysis',
  transform: round1,
})

export const geo = (
  path: string,
  key: string,
  unit?: AiHandoffFigure['unit']
): SectionField => ({ path, key, unit, source: 'geoportal' })

/**
 * What Popety's 0-100 parcel indices mean, in the payload's own words. The
 * under-exploited score is the one a reader most needs and least expects: 0 is
 * by far the most common value (about 70 % of parcels in Vaud), and it means
 * "no unused potential detected", which is also what the computation yields
 * when the zone's indices are unknown.
 */
export const POPETY_SCORE_DEFINITIONS: Record<string, string> = {
  under_exploited_score:
    "Popety index, 0-100: how much of the parcel's maximum authorised buildable potential is unused, from the zone's floor-area indices and the buildings already on it. Higher = more unused building rights (densification, extension, developer interest). 0 = no unused potential detected; this is also the value when the zone indices are unknown, so 0 does not prove the parcel is fully built.",
  development_score:
    'Popety index, 0-100: overall development potential of the parcel, combining zoning, location and current use. Higher = better development opportunity.',
  livability_score:
    'Popety index, 0-100: quality of the surroundings for living, from transport, amenities, noise and environment layers. Higher = better.',
}

export const calc = (
  path: string,
  key: string,
  unit?: AiHandoffFigure['unit']
): SectionField => ({ path, key, unit, source: 'spatial_analysis' })

const ESTIMATE_SPECS: SectionSpec[] = [
  {
    id: 'noise_analysis',
    title: 'Noise',
    // Names mirror /v1/lands/{id}/noise: max_db, avg_db, and dB(A) rather than
    // dB. The data is the federal SonBASE model, so it is credited as such —
    // calling it our own spatial analysis would understate where it comes from.
    provenance: {
      provider: 'SonBASE — Federal road & railway noise model (OFEV/BAFU)',
      reference_url: 'https://www.bafu.admin.ch/en/noise',
    },
    fields: [
      geo('road_day.avg', 'road.day.avg_db', 'dB(A)'),
      geo('road_day.max', 'road.day.max_db', 'dB(A)'),
      geo('road_night.avg', 'road.night.avg_db', 'dB(A)'),
      geo('road_night.max', 'road.night.max_db', 'dB(A)'),
      geo('railway_day.avg', 'railway.day.avg_db', 'dB(A)'),
      geo('railway_day.max', 'railway.day.max_db', 'dB(A)'),
      geo('railway_night.avg', 'railway.night.avg_db', 'dB(A)'),
      geo('railway_night.max', 'railway.night.max_db', 'dB(A)'),
    ],
  },
  {
    id: 'amenities',
    title: 'Amenities',
    fields: [
      calc('overall_score.value', 'overall_score', 'score'),
      calc('overall_score.category', 'overall_score_category'),
      calc('total_pois', 'poi_count', 'count'),
      calc('walking_radius', 'walking_radius_m', 'm'),
      calc('search_radius', 'search_radius_m', 'm'),
    ],
  },
  {
    id: 'transport_access',
    title: 'Transport access',
    // Key names mirror /v1/lands/{id}/transport-access: distance_m, headway_min,
    // mode, name — so a reader who has seen one has seen both.
    fields: [
      calc('scores.overall', 'overall_score', 'score'),
      calc('scores.publicTransport', 'public_transport.score', 'score'),
      calc('scores.roadAccess', 'road_access.score', 'score'),
      calc('scores.activeMobility', 'active_mobility.score', 'score'),
      geo(
        'publicTransportMetrics.qualityClass',
        'public_transport.quality_class'
      ),
      geo(
        'publicTransportMetrics.nearestBusStop.name',
        'nearest_bus_stop.name'
      ),
      geo(
        'publicTransportMetrics.nearestBusStop.distance',
        'nearest_bus_stop.distance_m',
        'm'
      ),
      geo(
        'publicTransportMetrics.nearestBusStop.headwayMin',
        'nearest_bus_stop.headway_min',
        'min'
      ),
      geo(
        'publicTransportMetrics.nearestTrainStation.name',
        'nearest_train_station.name'
      ),
      geo(
        'publicTransportMetrics.nearestTrainStation.distance',
        'nearest_train_station.distance_m',
        'm'
      ),
      geo(
        'publicTransportMetrics.determiningStop.name',
        'determining_stop.name'
      ),
      geo(
        'publicTransportMetrics.determiningStop.mode',
        'determining_stop.mode'
      ),
      geo(
        'publicTransportMetrics.determiningStop.distance',
        'determining_stop.distance_m',
        'm'
      ),
      geo(
        'publicTransportMetrics.determiningStop.headwayMin',
        'determining_stop.headway_min',
        'min'
      ),
      geo('roadAccessMetrics.nearestMainRoad.name', 'nearest_main_road.name'),
      geo(
        'roadAccessMetrics.nearestMainRoad.distance',
        'nearest_main_road.distance_m',
        'm'
      ),
    ],
  },
  {
    id: 'energy',
    title: 'Energy',
    // `solarGlobalExposure` and the solar efficiency figures are deliberately
    // absent: the stored values carry no unit anywhere we could verify, and
    // asserting a guessed kWh unit is the precise failure this payload exists
    // to prevent. Add them once the unit is confirmed against the app.
    fields: [
      geo('heaterGenerator', 'heating_system'),
      geo('powerSourceHeater', 'heating_energy_source'),
      geo('heaterWaterGenerator', 'water_heating_system'),
      geo('powerSourceWaterHeater', 'water_heating_energy_source'),
      geo('energyReferenceArea', 'energy_reference_area', 'm2'),
      calc('solarArea', 'solar_suitable_roof_area', 'm2'),
    ],
  },
  {
    id: 'sunlight',
    title: 'Daylight',
    // Astronomical daylight for the location — not measured sun exposure on the
    // building, which is why the key names say "daylight".
    fields: [
      calc('annual.totalDaylightHours', 'annual_daylight_hours', 'h'),
      calc('annual.averageDaylight', 'average_daily_daylight', 'h'),
      calc('annual.longestDay.hours', 'longest_day_daylight', 'h'),
      calc('annual.shortestDay.hours', 'shortest_day_daylight', 'h'),
    ],
  },
  {
    id: 'land',
    title: 'Parcel',
    // `lands[0].owners[]` holds real owner names. No path below goes near it —
    // `owner_count` is a count, not an identity — and a test asserts no owner
    // name can reach the output.
    definitions: POPETY_SCORE_DEFINITIONS,
    fields: [
      geo('lands.0.egrid_nb', 'egrid'),
      geo('lands.0.area', 'area_m2', 'm2'),
      geo('lands.0.official_area', 'official_area_m2', 'm2'),
      geo('lands.0.unit_nb', 'unit_count', 'count'),
      geo('lands.0.owner_nb', 'owner_count', 'count'),
      geo('lands.0.main_lupa_name', 'zone_name'),
      // Popety's own parcel indices. They were absent from the export until
      // 2026-09-07, which left every reader blind to the one figure that says
      // whether the parcel still carries unused building rights.
      calc('lands.0.under_exploited_score', 'under_exploited_score', 'score'),
      calc('lands.0.development_score', 'development_score', 'score'),
      calc('lands.0.livability_score', 'livability_score', 'score'),
    ],
  },
  {
    id: 'building',
    title: 'Building',
    // Same PII boundary as the parcel: `building.lands[].owners[]` is never read.
    fields: [
      geo('building.egid', 'egid'),
      geo('building.area', 'footprint_area_m2', 'm2'),
      geo('building.type', 'building_type'),
    ],
  },
  {
    id: 'taxes',
    title: 'Taxes',
    // Names follow /v1/lands/{id}/taxes: communal, cantonal, church_*. The
    // stored values are tax coefficients in percent of the base rate.
    provenance: { provider: 'Cantonal and communal tax registers' },
    fields: [
      geo('current_data.year', 'year'),
      geo('current_data.city_name', 'municipality'),
      geo('current_data.canton_name', 'canton'),
      geo('current_data.income_rate.income_rate_city', 'communal', 'pct'),
      geo('current_data.income_rate.income_rate_canton', 'cantonal', 'pct'),
      geo(
        'current_data.income_rate.income_rate_religion_protestant',
        'church_protestant',
        'pct'
      ),
      geo(
        'current_data.income_rate.income_rate_religion_roman',
        'church_roman_catholic',
        'pct'
      ),
    ],
  },
  {
    id: 'electricity',
    title: 'Electricity',
    // ElCom publishes Rp./kWh; the public API publishes CHF/kWh. The stored
    // values are Rp./kWh, so every price is divided by 100 to match — emitting
    // 32.841 under a CHF/kWh label would be wrong by two orders of magnitude.
    provenance: {
      provider: 'ElCom — Swiss Federal Electricity Commission',
      reference_url: 'https://www.strompreis.elcom.admin.ch',
    },
    fields: [
      geo('current_data.year', 'year'),
      geo('current_data.category', 'consumption_profile'),
      geo('annual_cost.consumption_kwh', 'consumption_kwh', 'kWh'),
      geo('annual_cost.cost_average', 'annual_cost_chf', 'CHF'),
      {
        path: 'current_data.grid_operators.0.prices.price_total',
        key: 'price_total_chf_kwh',
        unit: 'CHF/kWh',
        source: 'geoportal',
        transform: (v) => Math.round((v / 100) * 100000) / 100000,
      },
      {
        path: 'current_data.grid_operators.0.prices.price_energy',
        key: 'price_energy_chf_kwh',
        unit: 'CHF/kWh',
        source: 'geoportal',
        transform: (v) => Math.round((v / 100) * 100000) / 100000,
      },
    ],
  },
  {
    id: 'purchase_market',
    title: 'Purchase market',
    // Summary statistics over the comparable set. The quarterly buckets in
    // priceEvolutionPerSqm are deliberately not flattened — a hundred rows of
    // time series would bury the figures a reader actually asked for.
    fields: [
      calc('distribution.price_stats.count', 'sample_size', 'count'),
      money('distribution.price_stats.avg', 'average_price_chf'),
      money('distribution.price_stats.min', 'min_price_chf'),
      money('distribution.price_stats.max', 'max_price_chf'),
      area('distribution.area_stats.avg', 'average_area_m2'),
      rooms('distribution.rooms_stats.avg', 'average_rooms'),
      {
        path: 'priceEvolutionPerSqm.price_over_time.buckets',
        key: 'price_per_m2_over_time',
        source: 'spatial_analysis',
        kind: 'series',
        period_key: 'period',
        trend: {
          key: 'price_per_m2_trend',
          value_key: 'median_chf_per_m2',
          weight_key: 'sample_size',
        },
        item: [
          {
            path: 'doc_count',
            key: 'sample_size',
            unit: 'count',
            source: 'spatial_analysis',
          },
          {
            path: 'total_avg_price.value',
            key: 'average_chf_per_m2',
            unit: 'CHF/m2',
            source: 'spatial_analysis',
            transform: round0,
          },
          {
            path: 'overall_median.values.[50.0]',
            key: 'median_chf_per_m2',
            unit: 'CHF/m2',
            source: 'spatial_analysis',
            transform: round0,
          },
        ],
      },
    ],
  },
  {
    id: 'rental_market',
    title: 'Rental market',
    // Summary statistics over the comparable set. The quarterly buckets in
    // priceEvolutionPerSqm are deliberately not flattened — a hundred rows of
    // time series would bury the figures a reader actually asked for.
    fields: [
      calc('distribution.price_stats.count', 'sample_size', 'count'),
      money('distribution.price_stats.avg', 'average_rent_chf'),
      money('distribution.price_stats.min', 'min_rent_chf'),
      money('distribution.price_stats.max', 'max_rent_chf'),
      area('distribution.area_stats.avg', 'average_area_m2'),
      rooms('distribution.rooms_stats.avg', 'average_rooms'),
      {
        path: 'priceEvolutionPerSqm.price_over_time.buckets',
        key: 'price_per_m2_over_time',
        source: 'spatial_analysis',
        kind: 'series',
        period_key: 'period',
        trend: {
          key: 'price_per_m2_trend',
          value_key: 'median_chf_per_m2',
          weight_key: 'sample_size',
        },
        item: [
          {
            path: 'doc_count',
            key: 'sample_size',
            unit: 'count',
            source: 'spatial_analysis',
          },
          {
            path: 'total_avg_price.value',
            key: 'average_chf_per_m2',
            unit: 'CHF/m2',
            source: 'spatial_analysis',
            transform: round0,
          },
          {
            path: 'overall_median.values.[50.0]',
            key: 'median_chf_per_m2',
            unit: 'CHF/m2',
            source: 'spatial_analysis',
            transform: round0,
          },
        ],
      },
    ],
  },
  {
    id: 'building_permits',
    title: 'Building permits on the parcel',
    // The count, then the permits themselves, newest first, with their public
    // columns only. The rows also carry `development_classification_llm` and
    // its rationale, which are model output and must never be presented as a
    // source: they are not declared here, so they cannot be emitted.
    fields: [
      {
        path: '',
        key: 'permit_count',
        unit: 'count',
        source: 'geoportal',
        kind: 'length',
      },
      geo('0.publication_type_name', 'most_recent_type'),
      {
        path: '',
        key: 'permits',
        source: 'geoportal',
        kind: 'series',
        sort_desc_by: 'inquiry_start_date',
        limit: 15,
        item: [
          geo('inquiry_start_date', 'inquiry_date'),
          geo('inquiry_approval_date', 'approval_date'),
          geo('latest_status_db_normalized', 'status'),
          geo('official_final_decision', 'final_decision'),
          geo('publication_type_name', 'publication_type'),
          geo('work_type_official', 'work_type'),
          geo('regbl_classification_general', 'classification'),
          { ...geo('description', 'description'), max_chars: 300 },
          geo('municipality_name', 'municipality'),
          geo('primary_official_id', 'official_id'),
          geo('url_web', 'url'),
        ],
      },
    ],
  },
  {
    id: 'history',
    title: 'Property history',
    // `transactions[].transaction_raw_data`, `transaction_participants` and
    // `transaction_step_text` contain former owners' names, dates of birth and
    // addresses. Nothing here reads them, and a test asserts they can never
    // appear. The rows are declared column by column: dates, official ids,
    // published price, counts, the object described from the structured
    // properties, listing prices and rooms.
    provenance: { provider: 'Registre foncier / land registry publications' },
    fields: [
      {
        path: 'transactions',
        key: 'transaction_count',
        unit: 'count',
        source: 'registre_foncier',
        kind: 'length',
      },
      {
        path: 'listings',
        key: 'listing_count',
        unit: 'count',
        source: 'spatial_analysis',
        kind: 'length',
      },
      // Every land-registry mutation on the parcel, newest first. (The former
      // `last_transaction_*` fields read the first stored row, which is not
      // the latest one; the sorted rows below replace them.) The price is
      // published in a few cantons only: a null price means "not published",
      // never "given away".
      {
        path: 'transactions',
        key: 'transactions',
        source: 'registre_foncier',
        kind: 'series',
        sort_desc_by: 'transaction_date',
        limit: 15,
        item: [
          { path: 'transaction_date', key: 'date', source: 'registre_foncier' },
          {
            path: 'publication_date',
            key: 'publication_date',
            source: 'registre_foncier',
          },
          {
            path: 'transaction_step_name_normalized',
            key: 'type',
            source: 'registre_foncier',
          },
          {
            // the registry's own step label, e.g. "achat", "requête-vente"
            path: 'transaction_step_name',
            key: 'step',
            source: 'registre_foncier',
          },
          {
            // what was transferred, from the structured properties only
            path: 'transaction_properties',
            key: 'description',
            source: 'registre_foncier',
            derive: describeTransactionProperties,
          },
          {
            path: 'transaction_total_price',
            key: 'price',
            unit: 'CHF',
            source: 'registre_foncier',
            transform: (v) => (v > 0 ? v : null),
          },
          {
            path: 'buyers_count',
            key: 'buyers',
            unit: 'count',
            source: 'registre_foncier',
          },
          {
            path: 'sellers_count',
            key: 'sellers',
            unit: 'count',
            source: 'registre_foncier',
          },
          {
            path: 'properties_count',
            key: 'properties',
            unit: 'count',
            source: 'registre_foncier',
          },
          {
            path: 'official_id',
            key: 'official_id',
            source: 'registre_foncier',
          },
        ],
      },
      // Every listing found at the address on the portals, newest first. In a
      // building under PPE these can be other lots and lets: the address, the
      // rooms and the type are there for the reader to tell them apart.
      {
        path: 'listings',
        key: 'listings',
        source: 'spatial_analysis',
        kind: 'series',
        sort_desc_by: 'listing_timestamp',
        limit: 20,
        item: [
          {
            path: 'listing_timestamp',
            key: 'date',
            source: 'spatial_analysis',
          },
          { path: 'deal_type', key: 'deal_type', source: 'spatial_analysis' },
          {
            path: 'price',
            key: 'price',
            unit: 'CHF',
            unit_by: {
              path: 'deal_type',
              map: { rent: 'CHF/month', purchase: 'CHF', buy: 'CHF' },
            },
            source: 'spatial_analysis',
            transform: (v) => (v > 0 ? v : null),
          },
          {
            path: 'rooms_nb',
            key: 'rooms',
            unit: 'count',
            source: 'spatial_analysis',
          },
          {
            path: 'property_type',
            key: 'property_type',
            source: 'spatial_analysis',
          },
          { path: 'active', key: 'still_active', source: 'spatial_analysis' },
          { path: 'address', key: 'address', source: 'spatial_analysis' },
        ],
      },
    ],
  },
  {
    id: 'demographics',
    title: 'Demographics',
    // The stored `current` object carries roughly a hundred age-band and
    // household-size counts. Emitting them all would bury the figures a reader
    // asked for, so this is the headline set, named after /v1/lands/{id}/demographics.
    provenance: { provider: 'Federal Statistical Office (OFS/BFS)' },
    fields: [
      geo('current.year', 'year'),
      geo('current.municipality_name', 'municipality'),
      geo('current.region_name', 'canton'),
      geo('current.total_population', 'total', 'count'),
      {
        path: 'current',
        key: 'current_full',
        source: 'geoportal',
        kind: 'object',
      },
      {
        path: 'trends',
        key: 'population_over_time',
        source: 'geoportal',
        kind: 'series',
        item: [
          { path: 'year', key: 'year', source: 'geoportal' },
          {
            path: 'total_population',
            key: 'total',
            unit: 'count',
            source: 'geoportal',
          },
        ],
      },
    ],
  },
  {
    id: 'employment',
    title: 'Employment',
    // `trends` is stored oldest-first, so `-1` reads the most recent year.
    // Sector numbering follows the public API's primary/secondary/tertiary.
    provenance: { provider: 'Federal Statistical Office (OFS/BFS) — STATENT' },
    fields: [
      geo('trends.-1.year', 'year'),
      geo('trends.-1.total_employment', 'total_jobs', 'count'),
      geo('trends.-1.employment_sector_1', 'primary_jobs', 'count'),
      geo('trends.-1.employment_sector_2', 'secondary_jobs', 'count'),
      geo('trends.-1.employment_sector_3', 'tertiary_jobs', 'count'),
      geo('trends.-1.total_establishments', 'establishments', 'count'),
      {
        path: 'trends',
        key: 'employment_over_time',
        source: 'geoportal',
        kind: 'series',
        item: [
          { path: 'year', key: 'year', source: 'geoportal' },
          {
            path: 'total_employment',
            key: 'total_jobs',
            unit: 'count',
            source: 'geoportal',
          },
          {
            path: 'employment_sector_1',
            key: 'primary_jobs',
            unit: 'count',
            source: 'geoportal',
          },
          {
            path: 'employment_sector_2',
            key: 'secondary_jobs',
            unit: 'count',
            source: 'geoportal',
          },
          {
            path: 'employment_sector_3',
            key: 'tertiary_jobs',
            unit: 'count',
            source: 'geoportal',
          },
          {
            path: 'total_establishments',
            key: 'establishments',
            unit: 'count',
            source: 'geoportal',
          },
        ],
      },
    ],
  },
  {
    id: 'listings_comparables',
    title: 'Comparable listings',
    // The customer's own saved selection, in full. Capping this at three rows
    // and five fields was a human-readability instinct applied to a document
    // read by a model: it cannot compare a set it has not been given.
    // `broker_info.contact_names` is excluded — its `type` can be `private`,
    // in which case the name is a person's.
    fields: [
      {
        path: 'comparables',
        key: 'comparables',
        source: 'spatial_analysis',
        kind: 'series',
        item: [
          { path: 'title', key: 'title', source: 'spatial_analysis' },
          {
            path: 'municipality_name',
            key: 'municipality',
            source: 'spatial_analysis',
          },
          {
            path: 'postal_code',
            key: 'postal_code',
            source: 'spatial_analysis',
          },
          {
            path: 'district_name',
            key: 'district',
            source: 'spatial_analysis',
          },
          {
            path: 'property_type',
            key: 'property_type',
            source: 'spatial_analysis',
          },
          {
            path: 'property_category',
            key: 'property_category',
            source: 'spatial_analysis',
          },
          { path: 'deal_type', key: 'deal_type', source: 'spatial_analysis' },
          {
            path: 'price',
            key: 'price_chf',
            unit: 'CHF',
            source: 'spatial_analysis',
            transform: round0,
          },
          {
            path: 'area',
            key: 'area_m2',
            unit: 'm2',
            source: 'spatial_analysis',
            transform: round1,
          },
          {
            path: 'rooms_nb',
            key: 'rooms',
            unit: 'count',
            source: 'spatial_analysis',
          },
          {
            path: 'construction_year',
            key: 'construction_year',
            unit: 'year',
            source: 'spatial_analysis',
          },
          {
            path: 'listing_duration',
            key: 'days_on_market',
            unit: 'count',
            source: 'spatial_analysis',
          },
          {
            path: 'listing_timestamp',
            key: 'listed_at',
            source: 'spatial_analysis',
          },
          { path: 'ad_status', key: 'is_active', source: 'spatial_analysis' },
          {
            path: 'broker_info.type',
            key: 'broker_type',
            source: 'spatial_analysis',
          },
          {
            path: 'broker_info.mandate',
            key: 'broker_mandate',
            source: 'spatial_analysis',
          },
          {
            path: 'urls_image.0',
            key: 'image_url',
            source: 'spatial_analysis',
          },
        ],
      },
    ],
  },
  {
    id: 'transactions',
    title: 'Comparable transactions',
    provenance: { provider: 'Registre foncier / land registry publications' },
    // `transaction_participants`, `transaction_step_text` and
    // `transaction_raw_data` carry former owners' names and are never read;
    // the structured columns beside them are safe.
    fields: [
      {
        path: 'transactions',
        key: 'transactions',
        source: 'registre_foncier',
        kind: 'series',
        sort_desc_by: 'transaction_date',
        item: [
          { path: 'transaction_date', key: 'date', source: 'registre_foncier' },
          {
            path: 'publication_date',
            key: 'publication_date',
            source: 'registre_foncier',
          },
          {
            path: 'transaction_step_name_normalized',
            key: 'type',
            source: 'registre_foncier',
          },
          {
            // the registry's own step label, e.g. "achat", "requête-vente"
            path: 'transaction_step_name',
            key: 'step',
            source: 'registre_foncier',
          },
          {
            // what was transferred, from the structured properties only
            path: 'transaction_properties',
            key: 'description',
            source: 'registre_foncier',
            derive: describeTransactionProperties,
          },
          {
            // 0 means "price not published", not a free transfer
            path: 'transaction_total_price',
            key: 'price_chf',
            unit: 'CHF',
            source: 'registre_foncier',
            transform: (v) => (v > 0 ? v : null),
          },
          { path: 'address', key: 'address', source: 'registre_foncier' },
          {
            path: 'official_id',
            key: 'official_id',
            source: 'registre_foncier',
          },
          {
            path: 'transaction_properties.0.legal_type',
            key: 'legal_type',
            source: 'registre_foncier',
          },
          {
            path: 'transaction_properties.0.land_area',
            key: 'land_area_m2',
            unit: 'm2',
            source: 'registre_foncier',
            transform: round1,
          },
          {
            path: 'properties_count',
            key: 'properties',
            unit: 'count',
            source: 'registre_foncier',
          },
          {
            path: 'buyers_count',
            key: 'buyers',
            unit: 'count',
            source: 'registre_foncier',
          },
          {
            path: 'sellers_count',
            key: 'sellers',
            unit: 'count',
            source: 'registre_foncier',
          },
        ],
      },
    ],
  },
  {
    id: 'notes',
    title: 'Notes',
    fields: [
      {
        path: '',
        key: 'notes',
        source: 'user',
        kind: 'series',
        item: [
          { path: 'create_timestamp', key: 'written_on', source: 'user' },
          { path: 'author_name', key: 'author', source: 'user' },
          { path: 'content', key: 'content', source: 'user' },
        ],
      },
    ],
  },
  {
    id: 'files',
    title: 'Files',
    fields: [
      {
        path: '',
        key: 'files',
        source: 'user',
        kind: 'series',
        item: [
          { path: 'name', key: 'name', source: 'user' },
          { path: 'type', key: 'type', source: 'user' },
          { path: 'size', key: 'size_bytes', source: 'user' },
          { path: 'create_timestamp', key: 'added_on', source: 'user' },
        ],
      },
    ],
  },
]

export const SECTION_SPECS: SectionSpec[] = withDescriptions(
  ESTIMATE_SPECS,
  ESTIMATE_SECTION_DESCRIPTIONS
)
